/**
 * @fileoverview Compose explicitly invoked staff utilities and development/integration exercises. Focused components own upload links and manual case creation; backend endpoints own admission and mutation scope. Diagnostic status reads, bounded test setup, provider resync, and destructive reset are different actions. Navigation through this workspace does not itself run an integration or authorize a backfill.
 */

import React, { useContext, useEffect, useState } from 'react';
import FileUploadURL from './FileUploadURL';
import NewCase from './NewCase';
import Button from "../shared/AppButton";
import api from '../../api';
import ToastContext from '../../ToastContext';
import { Link, useNavigate } from 'react-router-dom';
import { AppPageHeader } from '../shared';
import AppIcon from "../shared/AppIcon";
import './Utilities.css';

const INFO_RETRIEVAL_TEST_RESET_CONFIRMATION = 'RESET INTAKE INFO TEST';
const INFO_RETRIEVAL_TEST_BASE = '/utilities/intake/info-retrieval-test';



function Utilities() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Utilities";
  });

  return (
    <div>
            <AppPageHeader
        eyebrow="Workspace Tools"
        title="Utilities"
        icon="pi pi-wrench text-xl" />

            <div className='px-4'>
                {/* Quick Actions */}
                <div className="mb-4 mt-3">
                    <div className="flex align-items-center mb-3">
                        <AppIcon icon="pi pi-bolt text-xl mr-2 text-500" />
                        <span className="text-2xl font-medium text-900">Quick Actions</span>
                    </div>
                    <div className='grid'>
                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-blue-100">
                                    <AppIcon icon="pi pi-plus-circle text-blue-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">New Case</span>
                            </div>
                            <NewCase />
                        </div>

                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-purple-100">
                                    <AppIcon icon="pi pi-link text-purple-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">File Upload URL</span>
                            </div>
                            <FileUploadURL />
                        </div>

                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-teal-100">
                                    <AppIcon icon="pi pi-envelope text-teal-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">Intake SMTP Password Test</span>
                            </div>
                            <IntakeSmtpPasswordTest />
                        </div>

                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-orange-100">
                                    <AppIcon icon="pi pi-sync text-orange-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">Clockify Resync</span>
                            </div>
                            <ResyncClockifyData />
                        </div>

                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6 border-1 border-red-200">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-red-100">
                                    <AppIcon icon="pi pi-trash text-red-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">Delete Intake Case Data</span>
                            </div>
                            <DeleteAllIntakeData />
                        </div>

                        <div className="utilities-card p-4 m-2 border-round col-12 lg:col-6 border-1 border-blue-200">
                            <div className="flex align-items-center mb-3">
                                <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-blue-100">
                                    <AppIcon icon="pi pi-file-import text-blue-600" style={{ fontSize: '0.9rem' }} />
                                </span>
                                <span className="text-xl font-medium text-900">Intake Information Retrieval Test Harness</span>
                            </div>
                            <InfoRetrievalTestHarness />
                        </div>
                    </div>
                </div>

                {/* Tools */}
                <div className="mb-4">
                    <div className="flex align-items-center mb-3">
                        <AppIcon icon="pi pi-wrench text-xl mr-2 text-500" />
                        <span className="text-2xl font-medium text-900">Tools</span>
                    </div>
                    <div className='grid'>
                        <div className="utilities-card utilities-card--link p-4 m-2 border-round col-12 lg:col-6" onClick={() => navigate('/utilities/signature-generator')} style={{ cursor: 'pointer' }}>
                            <div className="flex align-items-center justify-content-between">
                                <div className="flex align-items-center">
                                    <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-green-100">
                                        <AppIcon icon="pi pi-envelope text-green-600" style={{ fontSize: '0.9rem' }} />
                                    </span>
                                    <span className="text-xl font-medium text-900">Email Signature Generator</span>
                                </div>
                                <AppIcon icon="pi pi-arrow-right text-500" />
                            </div>
                            <div className="font-medium text-500 mt-3 ml-5 pl-2">Create a new email signature.</div>
                        </div>
                        <div className="utilities-card utilities-card--link p-4 m-2 border-round col-12 lg:col-6" onClick={() => navigate('/utilities/drive-dedup')} style={{ cursor: 'pointer' }}>
                            <div className="flex align-items-center justify-content-between">
                                <div className="flex align-items-center">
                                    <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-cyan-100">
                                        <AppIcon icon="pi pi-copy text-cyan-600" style={{ fontSize: '0.9rem' }} />
                                    </span>
                                    <span className="text-xl font-medium text-900">Drive Dedup</span>
                                </div>
                                <AppIcon icon="pi pi-arrow-right text-500" />
                            </div>
                            <div className="font-medium text-500 mt-3 ml-5 pl-2">Scan Google Drive matter folders for duplicate-file candidates.</div>
                        </div>
                        <div className="utilities-card utilities-card--link p-4 m-2 border-round col-12 lg:col-6" onClick={() => navigate('/utilities/simul-archive')} style={{ cursor: 'pointer' }}>
                            <div className="flex align-items-center justify-content-between">
                                <div className="flex align-items-center">
                                    <span className="inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle bg-indigo-100">
                                        <AppIcon icon="pi pi-history text-indigo-600" style={{ fontSize: '0.9rem' }} />
                                    </span>
                                    <span className="text-xl font-medium text-900">Simul Archive</span>
                                </div>
                                <AppIcon icon="pi pi-arrow-right text-500" />
                            </div>
                            <div className="font-medium text-500 mt-3 ml-5 pl-2">Browse, search, preview, and export archived Simul document versions.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>);

}

export default Utilities;



function IntakeSmtpPasswordTest() {
  const showToast = useContext(ToastContext);
  const [password, setPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const testPassword =

  (event) => {
    event.preventDefault();
    setTesting(true);
    setResult(null);
    api.post('/utilities/intake/mailer-test', { password })
      .then((response) => {
        setResult(response.data);
        if (response.data?.ok) {
          showToast({ severity: 'success', summary: 'SMTP Login Successful', detail: 'Gmail accepted the Intake SMTP password.' });
        } else {
          showToast({ severity: 'warn', summary: 'SMTP Login Failed', detail: response.data?.smtp_error || response.data?.error || 'Gmail rejected the password.' });
        }
      })
      .catch((error) => {
        const payload = error?.response?.data;
        setResult(payload || { ok: false, status: 'request_failed', error: error.message });
        showToast({ severity: 'error', summary: 'SMTP Test Failed', detail: payload?.error || error.message });
      })
      .finally(() => setTesting(false));
  };

  const resultBoxClass = result?.ok
    ? 'surface-50 border-1 border-green-200 border-round p-3 mt-3'
    : 'surface-50 border-1 border-red-200 border-round p-3 mt-3';
  const resultTitleClass = result?.ok ? 'font-medium text-green-600 mb-2' : 'font-medium text-red-600 mb-2';

  return (
    <form onSubmit={testPassword}>
      <label htmlFor="intake-smtp-test-password" className="block font-medium text-700 mb-2">App Password</label>
      <input
        id="intake-smtp-test-password"
        type="password"
        className="p-inputtext p-component w-full mb-3"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
      />
      <Button type="button" label={testing ? 'Testing Password' : 'Test Password'} icon="pi pi-send" loading={testing} disabled={testing || !password.trim()} onClick={testPassword} />
      {result && (
        <div className={resultBoxClass}>
          <div className={resultTitleClass}>
            {result.ok ? 'SMTP login succeeded.' : 'SMTP login failed.'}
          </div>
          <div className="font-medium text-500">
            {result.username} / {result.host}:{result.port} / {result.security}
          </div>
          <div className="font-medium text-500">
            Password length: {result.password_length}; spaces removed: {result.password_had_whitespace ? 'yes' : 'no'}
          </div>
          {result.smtp_code && <div className="font-medium text-500">SMTP code: {result.smtp_code}</div>}
          {(result.smtp_error || result.error) && <div className="font-medium text-500 mt-2">{result.smtp_error || result.error}</div>}
        </div>
      )}
    </form>
  );
}



function ResyncClockifyData() {
  const showToast = useContext(ToastContext);

  const resync =

  () => {
    api.post('/resync_clockify_billing_data')
    .then((response) => {
      console.log('billing:', response.data);
      showToast({ severity: 'success', summary: 'Successful', detail: 'Full Clockify Data Resync is Underway' });
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    });
  };

  return (
    <>
            <Button label="Resync All Clockify Data" onClick={resync} />
        </>);

}



function DeleteAllIntakeData() {
  const showToast = useContext(ToastContext);
  const [deleting, setDeleting] = useState(false);

  const deleteIntakeData =

  () => {
    const confirmation = window.prompt('Type DELETE INTAKE CASE DATA to permanently delete Intake leads, workflow runs, drafts, documents, events, timers, and sync state. Agent configuration is preserved.');
    if (confirmation === null) return;
    setDeleting(true);
    api.post('/utilities/intake/delete-all', { confirmation })
      .then((response) => {
        const deletedCounts = response.data?.deleted_counts || {};
        const deletedTotal = Object.values(deletedCounts).reduce((total, value) => total + Number(value || 0), 0);
        showToast({ severity: 'success', summary: 'Intake Case Data Deleted', detail: `Deleted ${deletedTotal} Intake rows.` });
      })
      .catch((error) => {
        console.error('Error deleting Intake data:', error);
        showToast({ severity: 'error', summary: 'Delete Intake Failed', detail: error?.response?.data?.error || error.message });
      })
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <div className="font-medium text-500 mb-3">Permanently removes Intake leads, workflow runs, drafts, documents, events, timers, and sync state. Agent configuration, prompts, models, roles, and workflow settings are preserved.</div>
      <Button label={deleting ? 'Deleting Intake Case Data' : 'Delete Intake Case Data'} icon="pi pi-trash" severity="danger" loading={deleting} disabled={deleting} onClick={deleteIntakeData} />
    </>
  );
}



function InfoRetrievalTestHarness() {
  const showToast = useContext(ToastContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workingAction, setWorkingAction] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');

  const loadStatus =

  () => {
    setLoading(true);
    return api.get(`${INFO_RETRIEVAL_TEST_BASE}/status`)
      .then((response) => setStatus(response.data))
      .catch((error) => {
        console.error('Error loading intake information retrieval test status:', error);
        showToast({ severity: 'error', summary: 'Intake Test Status Failed', detail: error?.response?.data?.error || error.message });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction =

  (actionKey, requestFactory, successSummary, onSuccess = null) => {
    setWorkingAction(actionKey);
    requestFactory()
      .then((response) => {
        setStatus(response.data);
        showToast({ severity: 'success', summary: successSummary, detail: response.data?.blocker || 'Intake test harness updated.' });
        if (onSuccess) onSuccess(response);
      })
      .catch(

      (error) => {
        console.error(`Intake information retrieval test ${actionKey} failed:`, error);
        const payload = error?.response?.data;
        if (payload?.lead || payload?.blocker) setStatus(payload);
        showToast({ severity: 'error', summary: 'Intake Test Failed', detail: payload?.error || error.message });
      })
      .finally(() => setWorkingAction(null));
  };

  const makeLead =

  () => runAction(
    'lead',
    () => api.post(`${INFO_RETRIEVAL_TEST_BASE}/lead`),
    'Test Lead Created'
  );

  const uploadDocuments =

  (variant) => runAction(
    `documents:${variant}`,
    () => api.post(`${INFO_RETRIEVAL_TEST_BASE}/documents`, { variant }),
    variant === 'correct' ? 'Correct Synthetic Documents Uploaded' : 'Wrong Synthetic Documents Uploaded'
  );

  const resetHarness =

  () => {
    setResetConfirmation('');
    setConfirmingReset(true);
  };

  const confirmResetHarness =

  () => {
    runAction(
      'reset',
      () => api.post(`${INFO_RETRIEVAL_TEST_BASE}/reset`, { confirmation: resetConfirmation }),
      'Intake Test Reset',
      () => {
        setConfirmingReset(false);
        setResetConfirmation('');
      }
    );
  };

  const lead = status?.lead;
  const uploadReady = Boolean(status?.upload_ready);
  const documentCounts = status?.document_counts || {};
  const disabled = loading || Boolean(workingAction);
  const uploadDisabled = disabled || !uploadReady;

  return (
    <div>
      <div className="font-medium text-500 mb-3">
        Creates a full-pipeline test lead, then lets you inject wrong or correct synthetic documents after the dry-run information request is recorded.
      </div>

      <div className="surface-50 border-1 surface-border border-round p-3 mb-3">
        {loading && !status ? (
          <div className="font-medium text-500">Loading intake test status...</div>
        ) : lead ? (
          <div className="flex flex-column gap-2">
            <div className="font-medium text-900">{lead.prospect_name}</div>
            <div className="font-medium text-500">
              {lead.prospect_email} / {lead.status} / {lead.current_stage}
            </div>
            {status?.lead_url && <Link to={status.lead_url} className="font-medium">Open Lead</Link>}
            <div className="font-medium text-500">
              Documents: {documentCounts.harness_active || 0} harness active / {documentCounts.pending_review || 0} pending review
            </div>
          </div>
        ) : (
          <div className="font-medium text-500">No intake information retrieval test lead exists.</div>
        )}
      </div>

      {status?.blocker && <div className="font-medium text-orange-600 mb-3">{status.blocker}</div>}
      {uploadReady && <div className="font-medium text-green-600 mb-3">Ready to upload synthetic documents.</div>}

      <div className="flex flex-wrap gap-2">
        <Button label={workingAction === 'lead' ? 'Making Test Lead' : 'Make Test Lead'} icon="pi pi-plus" loading={workingAction === 'lead'} disabled={disabled} onClick={makeLead} />
        <Button label={workingAction === 'documents:wrong' ? 'Uploading Wrong Documents' : 'Make Wrong Synthetic Documents And Upload'} icon="pi pi-times-circle" severity="warning" loading={workingAction === 'documents:wrong'} disabled={uploadDisabled} onClick={() => uploadDocuments('wrong')} />
        <Button label={workingAction === 'documents:correct' ? 'Uploading Correct Documents' : 'Make Correct Synthetic Documents And Upload'} icon="pi pi-check-circle" severity="success" loading={workingAction === 'documents:correct'} disabled={uploadDisabled} onClick={() => uploadDocuments('correct')} />
        <Button label={workingAction === 'reset' ? 'Resetting' : 'Reset'} icon="pi pi-refresh" severity="danger" loading={workingAction === 'reset'} disabled={disabled} onClick={resetHarness} />
      </div>

      {confirmingReset && (
        <div className="surface-50 border-1 border-red-200 border-round p-3 mt-3">
          <label htmlFor="intake-info-test-reset-confirmation" className="block font-medium text-700 mb-2">
            Type {INFO_RETRIEVAL_TEST_RESET_CONFIRMATION} to reset only this test harness data.
          </label>
          <input
            id="intake-info-test-reset-confirmation"
            className="p-inputtext p-component w-full mb-3"
            value={resetConfirmation}
            onChange={(event) => setResetConfirmation(event.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              label="Confirm Reset"
              icon="pi pi-check"
              severity="danger"
              loading={workingAction === 'reset'}
              disabled={disabled || resetConfirmation !== INFO_RETRIEVAL_TEST_RESET_CONFIRMATION}
              onClick={confirmResetHarness}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              disabled={disabled}
              onClick={() => {
                setConfirmingReset(false);
                setResetConfirmation('');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
